<template>
	<div class="flex gap-4">
		<div
			v-if="!showPopUp"
			class="flex relative items-center overflow-hidden lg:max-h-none"
		>
			<BigPicture
				:images="images"
				:big-image-index="bigImageIndex"
				:increase-big-image-index="increaseBigImageIndex"
				:decrease-big-image-index="decreaseBigImageIndex"
				:set-show-pop-up="setShowPopUp"
				size="small"
				@big-images-gallery-ref="bigImagesGalleryRef"
			/>

			<div
				v-if="bigImageIndex === 0"
				class="absolute top-0 left-0 w-full aspect-[2/3] pointer-events-none"
			>
				<ApplicationFlag
					v-for="(flagId, index) in productFlags"
					:key="flagId"
					:data="getFlagData(flagId)"
					:position="getItemPosition(flagId, index)"
					class="px-1"
				/>
			</div>

			<ProductSpecialPriceLabel
				v-if="priceListId"
				:price-list-id="priceListId"
				:special-price="specialPrice"
			/>

			<GalleryPagination
				v-if="!showPopUp && images.length > 1"
				:images="images"
				:big-image-index="bigImageIndex"
				:set-big-image-index="setBigImageIndex"
				class="lg:hidden absolute bottom-2 left-1/2 -translate-x-1/2"
			/>
		</div>

		<PopUp
			:images="images"
			:big-image-index="bigImageIndex"
			:increase-big-image-index="increaseBigImageIndex"
			:decrease-big-image-index="decreaseBigImageIndex"
			:show-pop-up="showPopUp"
			:set-show-pop-up="setShowPopUp"
		/>
	</div>
</template>

<script setup lang="ts">
import type { CategoryFiles } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import type { ProductFile } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type { FilteredProductItemFiles } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import { computed, ref, toRef } from "vue";
import type { SetShowPopUp } from "~/types/storyblok/imageGallery/imageGallery";
import type {
	ApplicationFlag,
	SpecialPrice
} from "~/types/storyblok/storyblok";
import { storeToRefs } from "pinia";

interface Props {
	images: ProductFile[] | CategoryFiles[] | FilteredProductItemFiles[];
	priceListId?: number | null;
	specialPrice?: SpecialPrice;
}
const props = defineProps<Props>();

const appStore = useAppStore();
const { selectedProduct, getParentFlagIds } = storeToRefs(useProductStore());

const productFlags = computed((): string[] => {
	const seed = selectedProduct.value?.FlagIdSeed;

	if (seed)
		return seed
			.split(",")
			.map((id) => id.trim())
			.filter(Boolean);

	return getParentFlagIds.value;
});

const { getItemPosition, getFlagData } = useApplicationFlags({
	appFlags: appStore.flags,
	productFlags: productFlags.value,
	offsetStartByPlacement: props.priceListId ? { top: 11 } : undefined
});

const images = toRef<
	() => ProductFile[] | CategoryFiles[] | FilteredProductItemFiles[]
>(() => props.images);
const bigImageIndex = ref<number>(0);
const showPopUp = ref<boolean>(false);
const bigImagesGallery = ref<HTMLElement[]>([]);

const setBigImageIndex = (index: number) => {
	bigImageIndex.value = index;
};

const bigImagesGalleryRef = (ref: { bigImagesGallery: Ref }) => {
	bigImagesGallery.value = ref.bigImagesGallery.value;
};
const increaseBigImageIndex = (value: number) => {
	if (value < images.value.length) {
		bigImageIndex.value = value;
	}
};
const decreaseBigImageIndex = (value: number) => {
	if (value >= 0) {
		bigImageIndex.value = value;
	}
};

const setShowPopUp: SetShowPopUp = (show) => {
	showPopUp.value = show;
};
</script>
